import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, CreditCard, Image as ImageIcon } from 'lucide-react';
import type { PaymentTypeToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { usePaymentTypesList, usePaymentTypeMutations, useOnlinePaymentTypes } from '~/hooks/use-payment-types';
import { useCountriesList } from '~/hooks/use-countries';
import { useCountryRegionsList } from '~/hooks/use-country-regions';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('el-GR'));

/** Payment types (Τρόποι Πληρωμής) list — parity with the legacy admin grid; standalone editor on add/edit. */
export function PaymentTypesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = usePaymentTypesList();
	const { data: onlineTypes } = useOnlinePaymentTypes();
	const { data: countries } = useCountriesList();
	const { data: regions } = useCountryRegionsList();
	const { remove } = usePaymentTypeMutations();

	const [deleteRow, setDeleteRow] = useState<PaymentTypeToken | null>(null);

	const onlineTypeName = useMemo(() => {
		const map = new Map<number, string>();
		for (const o of onlineTypes ?? []) if (o.id != null) map.set(o.id, o.nm ?? '');
		return (id?: number) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [onlineTypes]);

	const countryName = useMemo(() => {
		const map = new Map<number, string>();
		for (const c of countries ?? []) if (c.id != null) map.set(c.id, c.nm ?? '');
		return (id?: number | null) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [countries]);

	// All available countries for a payment type (BW-626); falls back to the legacy single cid.
	const countryNames = useMemo(() => {
		return (r: PaymentTypeToken) => {
			const ids = r.cids && r.cids.length > 0 ? r.cids : r.cid != null ? [r.cid] : [];
			return ids.length > 0 ? ids.map(id => countryName(id)).join(', ') : '—';
		};
	}, [countryName]);

	const regionName = useMemo(() => {
		const map = new Map<number, string>();
		for (const r of regions ?? []) if (r.id != null) map.set(r.id, r.nm ?? '');
		return (id?: number | null) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [regions]);

	// All available regions for a payment type (BW-627); falls back to the legacy single rid.
	const regionNames = useMemo(() => {
		return (r: PaymentTypeToken) => {
			const ids = r.rids && r.rids.length > 0 ? r.rids : r.rid != null ? [r.rid] : [];
			return ids.length > 0 ? ids.map(id => regionName(id)).join(', ') : '—';
		};
	}, [regionName]);

	const columns = useMemo<SystemGridColumn<PaymentTypeToken>[]>(
		() => [
			{
				key: 'image',
				header: t('orders.paymentTypes.columns.image'),
				width: '70px',
				toggleable: false,
				render: r =>
					r.img1 ? (
						<img src={r.img1} alt={r.nm ?? ''} className="h-9 w-9 rounded border border-border object-contain" />
					) : (
						<div className="flex h-9 w-9 items-center justify-center rounded border border-border bg-elevated">
							<ImageIcon size={14} className="text-foreground-disabled" />
						</div>
					)
			},
			{
				key: 'name',
				header: t('orders.paymentTypes.columns.name'),
				width: 'minmax(0,1.4fr)',
				sortValue: r => r.nm ?? '',
				exportValue: r => r.nm ?? '',
				render: r => (
					<span className="truncate text-sm font-medium text-foreground" title={r.nm ?? ''}>
						{r.nm || '—'}
					</span>
				)
			},
			{
				key: 'onlineType',
				header: t('orders.paymentTypes.columns.onlineType'),
				width: '150px',
				sortValue: r => onlineTypeName(r.opt),
				exportValue: r => onlineTypeName(r.opt),
				render: r => <span className="truncate text-sm text-foreground-muted">{onlineTypeName(r.opt)}</span>
			},
			{
				key: 'country',
				header: t('orders.paymentTypes.columns.countries'),
				width: '160px',
				sortValue: r => countryNames(r),
				exportValue: r => countryNames(r),
				render: r => (
					<span className="truncate text-sm text-foreground-muted" title={countryNames(r)}>
						{countryNames(r)}
					</span>
				)
			},
			{
				key: 'region',
				header: t('orders.paymentTypes.columns.regions'),
				width: '160px',
				sortValue: r => regionNames(r),
				exportValue: r => regionNames(r),
				render: r => (
					<span className="truncate text-sm text-foreground-muted" title={regionNames(r)}>
						{regionNames(r)}
					</span>
				)
			},
			{
				key: 'flatRate',
				header: t('orders.paymentTypes.columns.flatRate'),
				width: '120px',
				align: 'right',
				sortValue: r => r.frt ?? 0,
				exportValue: r => r.frt ?? '',
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{num(r.frt)}</span>
			},
			{
				key: 'threshold',
				header: t('orders.paymentTypes.columns.threshold'),
				width: '120px',
				align: 'right',
				defaultVisible: false,
				sortValue: r => r.tprc ?? 0,
				exportValue: r => r.tprc ?? '',
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{num(r.tprc)}</span>
			},
			{
				key: 'minPrice',
				header: t('orders.paymentTypes.columns.minPrice'),
				width: '120px',
				align: 'right',
				defaultVisible: false,
				sortValue: r => r.minprc ?? 0,
				exportValue: r => r.minprc ?? '',
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{num(r.minprc)}</span>
			},
			{
				key: 'maxPrice',
				header: t('orders.paymentTypes.columns.maxPrice'),
				width: '120px',
				align: 'right',
				defaultVisible: false,
				sortValue: r => r.maxprc ?? 0,
				exportValue: r => r.maxprc ?? '',
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{num(r.maxprc)}</span>
			},
			{
				key: 'retailOnly',
				header: t('orders.paymentTypes.columns.retailOnly'),
				width: '110px',
				align: 'center',
				sortValue: r => (r.retail_only ? 1 : 0),
				exportValue: r => (r.retail_only ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.retail_only)} />
			},
			{
				key: 'invoiceOnly',
				header: t('orders.paymentTypes.columns.invoiceOnly'),
				width: '110px',
				align: 'center',
				defaultVisible: false,
				sortValue: r => (r.invoice_only ? 1 : 0),
				exportValue: r => (r.invoice_only ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.invoice_only)} />
			},
			{
				key: 'visible',
				header: t('orders.paymentTypes.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.iv)} />
			},
			{
				key: 'erpCode',
				header: t('orders.paymentTypes.columns.erpCode'),
				width: '120px',
				defaultVisible: false,
				sortValue: r => r.erpcd ?? '',
				exportValue: r => r.erpcd ?? '',
				render: r => <span className="truncate font-mono text-xs text-foreground-muted">{r.erpcd || '—'}</span>
			},
			{
				key: 'sort',
				header: t('orders.paymentTypes.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si ?? 0}</span>
			}
		],
		[t, onlineTypeName, countryNames, regionNames]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<PaymentTypeToken>
				gridId="payment-types"
				title={t('orders.paymentTypes.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => `${r.nm ?? ''} ${r.cd ?? ''} ${r.erpcd ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<CreditCard size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('orders.paymentTypes.add')}
				onAdd={() => navigate('/orders/payment-methods/create')}
				onEdit={r => navigate(`/orders/payment-methods/${r.id}`)}
				onDelete={setDeleteRow}
				exportFilename="payment-types"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('orders.paymentTypes.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('orders.paymentTypes.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
